/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

import { useEffect, useState, useContext } from "react";
import {AuthContext} from "context";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Autocomplete } from "@mui/material";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import FormField from "layouts/applications/wizard/components/FormField";
import { useNavigate, useParams } from "react-router-dom";
import AttributeFields from "../create-part-category/attributeFields";
import partCategoryService from "services/part-category-service";

const EditPartCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [partCode, setPartCode] = useState({
    categoryCode: "",
    parentCategory: "",
    description: "",
    examples: "",
  });
  const [error, setError] = useState({
    categoryCode: false,
    parentCategory: false,
    description: false,
    examples: false,
    attributes: {},
  });
  const [value, setValue] = useState("");
  const [attributes, setAttributes] = useState({});
  const [showAttributes, setShowAttributes] = useState(false);
  const [numAttributes, setNumAttributes] = useState(0);
  const [disableAddAttribute, setDisableAddAttribute] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);

  const authContext = useContext(AuthContext);

  useEffect(() => {
    setIsVisible(true);
  }, []);


  useEffect(() => {
    (async () => {
      try {
        // Fetch & Set
        const response = await partCategoryService.getAllParentCategories();
        setCategories(response.data.attributes);
        const fetchPartCategory = await partCategoryService.getPartCategoryById(id);
        setPartCode(fetchPartCategory.data.attributes);

        if (fetchPartCategory.data.attributes.has_attributes) {
          if (fetchPartCategory.data.attributes.attributes[0] === undefined) { // Not all part cateogries have the TYPE attribute yet.
            setNumAttributes(Object.keys(fetchPartCategory.data.attributes.attributes).length + 1);
          } else {
            setNumAttributes(Object.keys(fetchPartCategory.data.attributes.attributes).length);
          }
          setAttributes(fetchPartCategory.data.attributes.attributes);
        } else {
          setNumAttributes(0);
        }
        setShowAttributes(true);



      } catch (err) {
        console.error(err);
        return null;
      }
    })();
  }, []);

  // Update the value of the parentCategory when an option is selected
  useEffect(() => {
    changeHandler({ target: { name: "parentCategory", value: value } });
  }, [value]);
  
  // Ensure the number of attributes is never negative 
  useEffect(() => {
    if (numAttributes < 0) {
      setNumAttributes(0);
    }
  }, [numAttributes]);

  // Enable the add attribute button only if the last added attribute is filled
  useEffect(() => {
    if (attributes[numAttributes-1] && attributes[numAttributes-1].attribute) {
      setDisableAddAttribute(false);
    }
  }, [attributes]);

  //Handles the changes of the input fields
  const changeHandler = (e) => {
    setPartCode({
      ...partCode,
      [e.target.name]: e.target.value,
    });
  };

  const resetErrors = () => {
    setError({
      categoryCode: false,
      parentCategory: false,
      description: false,
      examples: false,
    });
  }

  // Validates the fields before submission
  const fieldsValidator = () => {
    const requiredFields = ["categoryCode", "parentCategory", "description"];

    for (let field of requiredFields) {
      const errorname = "The " + field + " field is required";

      if (!partCode[field]) {
        setError({ ...error, [field]: true, textError: errorname });
        return false;
      }
    }
    return true;
  }

  // Handles the submission of the form
  const submitHandler = async (e) => {

    e.preventDefault();
    // Validate all the fields before allowing submission
    resetErrors();
    if (!fieldsValidator()) {
      console.log("Missing a field", error);
      return;
    }

    const newPartCategory = {
      data: {
        type: "categoryCode",
        orderChanged: orderChanged,
        attributes: {
          categoryCode: partCode.categoryCode,
          parentCategory: partCode.parentCategory,
          description: partCode.description,
          examples: partCode.examples,
          attributes: partCode.attributes,
          has_attributes: numAttributes > 0 ? true : false, // Legacy support for part categories without attributes
        },
        logs: {
          user: await authContext.getCurrentUser(),
          comment: ""
        }
      }
    };

    try {
      await partCategoryService.editPartCategory(newPartCategory, id);
      navigate("/part-code-management/part-category-view", {
        state: { value: false, text: "" },
      });
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  const handleAddAttribute = () => {
    setNumAttributes(numAttributes + 1);
    setDisableAddAttribute(true);
  }
  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle={partCode.categoryCode} />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>

            {/* Title and Description */}
            <MDBox
              mt={6}
              mb={8}
              textAlign="center"
              sx={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
              }}
            >
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  Edit Part Category.
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="regular" color="secondary">
                Please remember to apply the changes by clicking the Save button.
              </MDTypography>
            </MDBox>

            {/* Form Card */}
            <Card
              sx={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
              }}
            >
              <MDBox
                component="form"
                method="POST"
                onSubmit={submitHandler}
                encType="multipart/form-data"
              >
                <MDBox display="flex" flexDirection="column" px={3} my={4}>

                  <Grid container spacing={3}>

                    {/* Part Category Code */}
                    <Grid item xs={12} md={6}>
                      <FormField
                        label="Part Category Code"
                        placeholder="XXX"
                        name="categoryCode"
                        value={partCode.categoryCode}
                        onChange={changeHandler}
                        disabled
                        inputProps={{
                          autoComplete: "categoryCode",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                      />
                      {error.categoryCode && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>

                    {/* Parent Category */}
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        defaultValue=""
                        options={categories}
                        getOptionLabel={(option) => option}
                        value={partCode.parentCategory ?? ""}
                        onChange={(event, newValue) => {
                          setValue(newValue);
                        }}
                        renderInput={(params) => (
                          <FormField {...params} label="Parent Category" InputLabelProps={{ shrink: true }} />
                        )}
                      />
                      {error.parentCategory && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>

                  </Grid>

                  <Grid container spacing={3} mt={2}>

                    {/* Full Part Category */}
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Full Part Category"
                        placeholder=" "
                        name="description"
                        value={partCode.description}
                        onChange={changeHandler}
                        multiline rows={5}
                        fullWidth
                        inputProps={{
                          autoComplete: "description",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                      />
                      {error.description && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>

                    {/* Examples and Notes */}
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Examples and Notes"
                        placeholder=" "
                        name="examples"
                        value={partCode.examples}
                        onChange={changeHandler}
                        multiline rows={5}
                        fullWidth
                        inputProps={{
                          autoComplete: "examples",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                      />
                      {error.examples && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>
                  </Grid>

                  {/* Change this to be condional based on the number of attributes */}
                  {showAttributes && (
                    <AttributeFields
                      id={id}
                      numAttributes={numAttributes}
                      setNumAttributes={setNumAttributes}
                      changeHandler={changeHandler}
                      editMode={true}
                      attributesData={attributes}
                      attributes={attributes}
                      setAttributes={setAttributes}
                      setOrderChanged={setOrderChanged}
                    />
                  )}


                  {/* Form Action Buttons*/}
                  <MDBox mt={2} mb={2} display="flex" justifyContent="space-between">

                    {/* Add Attribute Button */}
                    <MDBox>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        disabled={disableAddAttribute}
                        onClick={handleAddAttribute}
                      >
                        Add an Attribute
                      </MDButton>
                    </MDBox>

                    {/* Cancel & Save Buttons */}
                    <MDBox display="flex">
                      <MDBox mx={2}>

                        {/* Cancel Button */}
                        <MDButton
                          variant="gradient"
                          color="dark"
                          size="small"
                          px={2}
                          mx={2}
                          onClick={() =>
                            navigate("/part-code-management/part-category-view", {
                              state: { value: false, text: "" },
                            })
                          }
                        >
                          Cancel
                        </MDButton>
                      </MDBox>

                      {/* Submit Button */}
                      <MDButton variant="gradient" color="dark" size="small" type="submit">
                        Save
                      </MDButton>

                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default EditPartCategory;
